import {
  type VendorReferredResponse,
  VendorReferredResponse as VendorReferredResponseSchema,
} from '@uit/shared/schemas/referral';
import {
  type VendorAchResponse,
  VendorAchResponse as VendorAchResponseSchema,
  type VendorDetail,
  VendorDetail as VendorDetailSchema,
  isVendorComplete,
} from '@uit/shared/schemas/vendor';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { QuickbooksLinkSection } from '../components/quickbooks/QuickbooksLinkSection.js';
import { DeleteDocumentButton } from '../components/DeleteDocumentButton.js';
import { useApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { COMPANY_LABELS, DOC_LABELS } from '../lib/labels.js';
import s from './VendorDetailPage.module.scss';

export function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  // One schema for both shapes — engagement rate_to_client is optional, so the
  // non-owner response parses too (T-186).
  const { data, error, loading, refetch } = useApi<VendorDetail>(
    id ? `/api/vendors/${id}` : null,
    VendorDetailSchema,
  );

  // Admin-only, on-demand full-ACH reveal for QB Bill Pay entry (T-077).
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const [ach, setAch] = useState<VendorAchResponse | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  // The resources this vendor referred (T-155). Admin-only; the section only
  // renders when the list is non-empty, so non-referrer vendors show nothing.
  const { data: referred } = useApi<VendorReferredResponse>(
    isAdmin && id ? `/api/vendors/${id}/referred` : null,
    VendorReferredResponseSchema,
  );

  const revealAch = async (): Promise<void> => {
    if (!id) return;
    setRevealing(true);
    setRevealError(null);
    try {
      const r = await fetch(`/api/vendors/${id}/ach`, { credentials: 'include' });
      if (!r.ok) throw new Error(String(r.status));
      setAch(VendorAchResponseSchema.parse(await r.json()));
    } catch {
      setRevealError('Could not reveal ACH.');
    } finally {
      setRevealing(false);
    }
  };

  if (loading && !data) return <div className={s.loading}>Loading…</div>;
  if (error || !data) {
    const is404 = error?.message.includes('404');
    return (
      <div className={s.notFound}>
        <Link to="/" className={s.back}>
          ← Back to vendors
        </Link>
        <div className={s.notFoundBody}>
          {is404 ? 'Vendor not found.' : `Failed to load: ${error?.message ?? 'unknown'}`}
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <Link to="/" className={s.back}>
        ← Back to vendors
      </Link>

      <div className={s.header}>
        <h1 className={s.title}>
          {data.business_name}
          {!isVendorComplete(data) && <span className={s.incomplete}>Incomplete</span>}
        </h1>
        <p className={s.subtitle}>
          {data.company_type
            ? (COMPANY_LABELS[data.company_type] ?? data.company_type)
            : 'Company type unknown'}
          {' · '}
          {data.resources.length} resource{data.resources.length === 1 ? '' : 's'}
        </p>
      </div>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Business</h2>
        <dl className={s.grid}>
          <Field label="Business email" value={data.business_email} />
          <Field label="Mobile phone" value={data.mobile_phone} />
          <Field label="Office phone" value={data.office_phone} />
          <Field label="State of incorporation" value={data.state_of_incorporation} />
        </dl>
        <h3 className={s.subhead}>Business address</h3>
        <p className={s.addr}>
          {data.business_address_street}
          <br />
          {data.business_address_city}, {data.business_address_state} {data.business_address_zip}
        </p>
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Banking (masked)</h2>
        <p className={s.maskNote}>
          ACH and EIN are encrypted at rest; only the last 4 digits are shown.
        </p>
        <dl className={s.grid}>
          <Field
            label="ACH routing"
            value={ach ? ach.ach_routing : data.ach_routing_masked}
            mono
          />
          <Field
            label="ACH account"
            value={ach ? ach.ach_account : data.ach_account_masked}
            mono
          />
          <Field label="EIN" value={data.ein_masked} mono />
        </dl>
        {isAdmin && (data.ach_routing_masked || data.ach_account_masked) && (
          <button
            type="button"
            className={s.revealBtn}
            disabled={revealing}
            onClick={() => {
              if (ach) setAch(null);
              else void revealAch();
            }}
          >
            {ach ? 'Hide ACH' : revealing ? 'Revealing…' : 'Show full ACH'}
          </button>
        )}
        {revealError && (
          <p className={s.maskNote} role="alert">
            {revealError}
          </p>
        )}
      </section>

      {isAdmin && <QuickbooksLinkSection vendorId={data.id} />}

      {data.documents.length > 0 && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Business documents</h2>
          <DocList docs={data.documents} onDeleted={() => void refetch()} />
        </section>
      )}

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Resources</h2>
        {data.resources.length === 0 ? (
          <p className={s.muted}>No resources yet.</p>
        ) : (
          data.resources.map((r) => (
            <div key={r.id} className={s.resourceCard}>
              <div className={s.resourceHead}>
                <h3 className={s.resourceName}>{r.resource_name}</h3>
                {r.role && <span className={s.resourceMeta}>{r.role}</span>}
              </div>
              <p className={s.resourceMeta} style={{ margin: 0 }}>
                {r.engagements.length} engagement{r.engagements.length === 1 ? '' : 's'}
                {' · '}
                <Link to={`/resources/${r.id}`} className={s.docLink}>
                  View resource →
                </Link>
              </p>
            </div>
          ))
        )}
      </section>

      {isAdmin && referred && referred.referred.length > 0 && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Referred resources</h2>
          <p className={s.muted} style={{ marginTop: 0 }}>
            Resources this vendor referred (named as the referrer on their engagement).
          </p>
          {referred.referred.map((x) => (
            <div key={x.engagement_id} className={s.resourceCard}>
              <div className={s.resourceHead}>
                <h3 className={s.resourceName}>{x.resource_name}</h3>
                <span className={s.resourceMeta}>{x.business_name}</span>
              </div>
              <p className={s.resourceMeta} style={{ margin: 0 }}>
                {x.client_name ?? '—'} · {x.product_service}
                {' · '}
                <Link to={`/resources/${x.resource_id}`} className={s.docLink}>
                  View resource →
                </Link>
              </p>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function DocList({ docs, onDeleted }: { docs: VendorDetail['documents']; onDeleted: () => void }) {
  return (
    <ul className={s.docList}>
      {docs.map((d) => (
        <li key={d.id} className={s.docRow}>
          <span className={s.docKind}>{DOC_LABELS[d.kind] ?? d.kind}</span>
          <a className={s.docLink} href={d.download_url} target="_blank" rel="noreferrer">
            {d.original_filename}
          </a>
          <DeleteDocumentButton id={d.id} filename={d.original_filename} onDeleted={onDeleted} />
        </li>
      ))}
    </ul>
  );
}

function Field({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? s.mono : undefined}>{value ?? <span className={s.muted}>—</span>}</dd>
    </div>
  );
}
